import { useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle2, Clock, RefreshCw, XCircle } from 'lucide-react';
import { trpc } from '@/lib/trpc';

export default function IntegrationStatus() {
  const [history, setHistory] = useState<any[]>([]);

  const { data: health, isLoading, refetch, isFetching } = trpc.settings.healthCheck.useQuery(undefined, {
    refetchInterval: 60000,
    onSuccess: (data: any) => {
      setHistory((prev) => [data, ...prev.slice(0, 9)]);
    },
  } as any);

  const handleRefresh = () => refetch();

  const getStatusColor = (status?: string) => {
    if (status === 'healthy') return 'bg-green-50 border-green-200';
    if (status === 'degraded') return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  const getStatusIcon = (status?: string) => {
    if (status === 'healthy') return <CheckCircle2 className="w-5 h-5 text-green-600" />;
    if (status === 'degraded') return <AlertCircle className="w-5 h-5 text-yellow-600" />;
    return <XCircle className="w-5 h-5 text-red-600" />;
  };

  const getCheckIcon = (passed: boolean) =>
    passed
      ? <CheckCircle2 className="w-4 h-4 text-green-600" />
      : <XCircle className="w-4 h-4 text-red-600" />;

  const loading = isLoading || isFetching;

  return (
    <DashboardLayout>
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Status da Integração WhatsApp</h1>
          <p className="text-gray-600 mt-1">Monitoramento em tempo real da conexão com Meta Cloud API</p>
        </div>
        <Button onClick={handleRefresh} disabled={loading} className="gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Verificando...' : 'Verificar Agora'}
        </Button>
      </div>

      {isLoading && !health && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            Verificando integração...
          </CardContent>
        </Card>
      )}

      {health && (
        <Card className={`border-2 ${getStatusColor(health.status)}`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getStatusIcon(health.status)}
                <div>
                  <CardTitle className="capitalize">{health.status}</CardTitle>
                  <CardDescription>
                    Última verificação: {new Date(health.timestamp).toLocaleString('pt-BR')}
                  </CardDescription>
                </div>
              </div>
              <Badge
                variant={
                  health.status === 'healthy' ? 'default'
                    : health.status === 'degraded' ? 'secondary'
                    : 'destructive'
                }
              >
                {health.status === 'healthy' ? '✓ Operacional'
                  : health.status === 'degraded' ? '⚠ Degradado'
                  : '✗ Crítico'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              <h3 className="font-semibold text-sm">Componentes</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-center gap-2 p-3 bg-white rounded border">
                  {getCheckIcon(health.checks.tokenValid)}
                  <span className="text-sm">Token de Acesso Válido</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-white rounded border">
                  {getCheckIcon(health.checks.wabaAccessible)}
                  <span className="text-sm">WABA Acessível</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-white rounded border">
                  {getCheckIcon(health.checks.phoneNumberAccessible)}
                  <span className="text-sm">Número de Telefone Acessível</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-white rounded border">
                  {getCheckIcon(health.checks.webhookConfigured)}
                  <span className="text-sm">Webhook Configurado</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-white rounded border col-span-1 md:col-span-2">
                  {getCheckIcon(health.checks.canSendMessages)}
                  <span className="text-sm">Capacidade de Envio de Mensagens</span>
                </div>
              </div>
            </div>

            {health.errors.length > 0 && (
              <div className="space-y-2 p-3 bg-red-50 rounded border border-red-200">
                <h3 className="font-semibold text-sm text-red-900">Erros</h3>
                <ul className="space-y-1">
                  {health.errors.map((error: string, i: number) => (
                    <li key={i} className="text-sm text-red-700">• {error}</li>
                  ))}
                </ul>
              </div>
            )}

            {health.warnings.length > 0 && (
              <div className="space-y-2 p-3 bg-yellow-50 rounded border border-yellow-200">
                <h3 className="font-semibold text-sm text-yellow-900">Avisos</h3>
                <ul className="space-y-1">
                  {health.warnings.map((w: string, i: number) => (
                    <li key={i} className="text-sm text-yellow-700">• {w}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Histórico de Verificações</CardTitle>
            <CardDescription>Últimas 10 verificações de saúde</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {history.map((check, i) => (
                <div key={i} className="flex items-center justify-between p-3 border rounded">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(check.status)}
                    <div>
                      <p className="text-sm font-medium capitalize">{check.status}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(check.timestamp).toLocaleString('pt-BR')}
                      </p>
                    </div>
                  </div>
                  <Badge variant={check.status === 'healthy' ? 'default' : check.status === 'degraded' ? 'secondary' : 'destructive'}>
                    {check.errors.length} erro{check.errors.length !== 1 ? 's' : ''}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Documentação</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <h4 className="font-semibold text-sm mb-2">O que significa cada status?</h4>
            <ul className="space-y-2 text-sm">
              <li><strong className="text-green-600">✓ Healthy:</strong> Todos os componentes estão funcionando corretamente</li>
              <li><strong className="text-yellow-600">⚠ Degraded:</strong> Alguns componentes têm problemas, mas o sistema continua funcionando</li>
              <li><strong className="text-red-600">✗ Unhealthy:</strong> Componentes críticos falharam, o sistema não pode enviar mensagens</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-sm mb-2">Ações Recomendadas</h4>
            <ul className="space-y-2 text-sm">
              <li>• Se o status for "Unhealthy", verifique o token de acesso no Meta for Developers</li>
              <li>• Certifique-se de que o webhook está configurado corretamente</li>
              <li>• Verifique se o número de telefone está ativo na conta WhatsApp Business</li>
              <li>• Consulte o histórico de verificações para identificar padrões de falha</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
    </DashboardLayout>
  );
}
